import { useSearchParams } from 'react-router';
import { useEffect, useState } from 'react';
import { ProductDetails } from './sections/ProductDetails';
import { PricesAndAvailability } from './sections/PricesAndAvailability';
import {
  useGetSellerProductInventoriesForBuyer,
  useGetBuyerProductInventory,
} from '~/api/inventory';
import type {
  Cart,
  Inventory,
  PlatformProduct,
  PlatformProductLength,
  SellerProductInventory,
} from '~/graphql/graphql';
import { useGetCart } from '~/api';
import { useGetSellerInventoryProductLengthsByPlatformProduct } from '~/api/products-length';
import InventoryRequirement from '~/pages/order-builder/sections/InventoryRequirement';
import { OrderCalculator } from './sections/OrderCalculator';
import { useDispatch, useSelector } from 'react-redux';
import { setInventory } from '~/redux/reducers/inventory';
import { setCarts } from '~/redux/reducers/cart';
import { BackButton } from '~/components/ui';
import { useTranslation } from 'react-i18next';
import { useGetPlatformProductById } from '~/api/platformProducts';
import type { RootState } from '~/redux/store';
import { unitsPerPiece, convertPiecesToUnit } from '~/utils/unitConversion';
import { formatCurrency } from '~/utils';

export type OrderInfo = {
  targetPrice: number;
  targetOrderSize: number;
  targetWeeksToCover: number;
};

function getAverageNPrice(
  sortedSellerProductInventories: SellerProductInventory[]
) {
  const nCount = Math.min(3, sortedSellerProductInventories?.length || 0);
  const totalCost =
    sortedSellerProductInventories
      ?.slice(0, nCount)
      ?.reduce(
        (acc, inventory) =>
          acc + (inventory?.sellerProductConfiguration?.pricePerUnit || 0),
        0
      ) || 0;
  const averageNPrice = parseFloat(
    (nCount > 0 ? totalCost / nCount : 0).toFixed(2)
  );

  return { averageNPrice, nCount };
}
type OrderBuilderProps = {
  platformProductId: string;
  inventoryId: string | null;
  platformProductLengthId: string | null;
  returnPath: string;
};
function OrderBuilderPage({
  platformProductId,
  inventoryId,
  platformProductLengthId,
  returnPath,
}: Readonly<OrderBuilderProps>) {
  const { t } = useTranslation('order-builder');
  const [selectedLength, setSelectedLength] = useState<string | undefined>(
    undefined
  );
  const [orderInfo, setOrderInfo] = useState({
    targetPrice: 0,
    targetOrderSize: 0,
    targetWeeksToCover: 0,
  });

  const locationId = useSelector(
    (state: RootState) => state.cart.selectedLocationId
  );

  const platformProductIdForQuery =
    selectedLength === undefined ? platformProductId : undefined;

  const { data: sellerProductInventories } =
    useGetSellerProductInventoriesForBuyer(
      selectedLength,
      platformProductIdForQuery
    );

  const { data: platformProductLengths } =
    useGetSellerInventoryProductLengthsByPlatformProduct(platformProductId);
  const latestPriceMid =
    platformProductLengths?.sellerInventoryProductLengthsByPlatformProduct?.find(
      (ppl) => ppl.id === selectedLength
    )?.latestPriceMid;
  const formattedMarketMidPrice = latestPriceMid
    ? formatCurrency(latestPriceMid) + '/MBF'
    : 'N/A';

  const { data: cartByLocation } = useGetCart(locationId);

  const { data: inventory, refetch } = useGetBuyerProductInventory(
    inventoryId ?? ''
  );

  const { data: platformProductData } =
    useGetPlatformProductById(platformProductId);
  const product = platformProductData?.platformProduct as PlatformProduct;

  useEffect(() => {
    if (inventoryId) {
      // ensure data is fetched when the query param becomes available
      void refetch?.();
    }
  }, [inventoryId, refetch]);

  const sortedSellerProductInventories =
    sellerProductInventories?.sellerProductInventoriesForBuyer?.sort(
      (a, b) =>
        (a?.sellerProductConfiguration?.pricePerUnit ?? 0) -
        (b?.sellerProductConfiguration?.pricePerUnit ?? 0)
    );

  const dispatch = useDispatch();

  const handleOrderInfoChange = (name: string, value: string) => {
    const valueNumber = Number(value);
    const usageRatePerWeek =
      inventory?.buyerProductInventory?.buyerInventoryConfiguration
        ?.usageRatePerWeek?.value || 0;

    if (name === 'targetWeeksToCover') {
      const targetOrderSize = parseFloat(
        (usageRatePerWeek * valueNumber).toFixed(2)
      );
      setOrderInfo((prev) => ({
        ...prev,
        targetWeeksToCover: valueNumber,
        targetOrderSize,
      }));
      return;
    }

    if (name === 'targetOrderSize') {
      const targetWeeksToCover =
        usageRatePerWeek > 0
          ? parseFloat((valueNumber / usageRatePerWeek).toFixed(2))
          : 0;
      setOrderInfo((prev) => ({
        ...prev,
        targetOrderSize: valueNumber,
        targetWeeksToCover,
      }));
      return;
    }
    setOrderInfo((prev) => ({ ...prev, [name]: valueNumber }));
  };

  const { nCount, averageNPrice } = getAverageNPrice(
    sortedSellerProductInventories as SellerProductInventory[]
  );

  // set defaults for filters
  useEffect(() => {
    function setDefaultPlatformProductLength() {
      if (platformProductLengthId) {
        setSelectedLength(platformProductLengthId);
      }
    }
    setDefaultPlatformProductLength();
  }, [platformProductLengthId]);

  // set defaults for order info based on inventory configuration
  useEffect(() => {
    function setDefaultOrderInfo() {
      if (inventory?.buyerProductInventory) {
        const usageRatePerWeek =
          inventory.buyerProductInventory.buyerInventoryConfiguration
            ?.usageRatePerWeek?.value || 0;
        const targetWeeksCovered =
          inventory.buyerProductInventory.buyerInventoryConfiguration
            ?.targetWeeksCovered || 0;
        const currentStock =
          inventory.buyerProductInventory.quantity.value || 0;

        let targetOrderSize =
          usageRatePerWeek * targetWeeksCovered - currentStock;
        targetOrderSize = targetOrderSize < 0 ? 0 : targetOrderSize;
        const flooredTargetOrderSize = Math.floor(targetOrderSize);
        const targetWeeksToCover =
          usageRatePerWeek > 0
            ? parseFloat((flooredTargetOrderSize / usageRatePerWeek).toFixed(2))
            : 0;
        const { averageNPrice } = getAverageNPrice(
          sortedSellerProductInventories as SellerProductInventory[]
        );
        setOrderInfo({
          targetOrderSize: flooredTargetOrderSize,
          targetWeeksToCover,
          targetPrice: Number(averageNPrice),
        });
      }
    }
    setDefaultOrderInfo();
    dispatch(setInventory(inventory?.buyerProductInventory as Inventory));
  }, [
    dispatch,
    inventory?.buyerProductInventory,
    sortedSellerProductInventories,
  ]);

  useEffect(() => {
    dispatch(setCarts((cartByLocation?.carts as Cart[]) || []));
  }, [dispatch, cartByLocation?.carts]);

  const listingCount = sortedSellerProductInventories?.length || 0;

  const uniqueSellerIds = new Set();
  sortedSellerProductInventories?.forEach((sellerInventory) => {
    uniqueSellerIds.add(sellerInventory?.organization.id);
  });
  const sellersCount = uniqueSellerIds.size;

  const handleLengthFilterChange = (_: string, value: string) => {
    if (value === 'all') {
      setSelectedLength(undefined);
      return;
    }
    setSelectedLength(value);
  };

  function roundUpToBundleQuantity(rawQty: number): number {
    const piecesPerBundle = product?.piecesPerBundle.value;
    if (!piecesPerBundle || rawQty <= 0) return rawQty;

    const lengthData =
      product?.lengths?.find((l) => l?.id === selectedLength) ??
      product?.lengths?.[0];
    const lengthInches =
      product?.actualLengthInches ?? lengthData?.actualLengthInches ?? 0;

    const widthInches = product?.isNominal
      ? (product.nominalWidthInches ?? product.actualWidthInches ?? 0)
      : (product.actualWidthInches ?? 0);
    const thicknessInches = product?.isNominal
      ? (product.nominalThicknessInches ?? product.actualThicknessInches ?? 0)
      : (product.actualThicknessInches ?? 0);

    if (!lengthInches || !widthInches || !thicknessInches) return rawQty;

    const pieces =
      rawQty / unitsPerPiece('MBF', lengthInches, widthInches, thicknessInches);
    const wholeBundles = Math.ceil(pieces / piecesPerBundle);
    return convertPiecesToUnit(
      wholeBundles * piecesPerBundle,
      'MBF',
      lengthInches,
      widthInches,
      thicknessInches
    );
  }

  const handleTargetOrderSizeBlur = () => {
    const usageRatePerWeek =
      inventory?.buyerProductInventory?.buyerInventoryConfiguration
        ?.usageRatePerWeek?.value || 0;
    setOrderInfo((prev) => {
      const rounded = roundUpToBundleQuantity(prev.targetOrderSize);
      if (rounded === prev.targetOrderSize) return prev;
      const targetWeeksToCover =
        usageRatePerWeek > 0
          ? parseFloat((rounded / usageRatePerWeek).toFixed(2))
          : prev.targetWeeksToCover;
      return { ...prev, targetOrderSize: rounded, targetWeeksToCover };
    });
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 sm:gap-3 mb-1">
            <div data-testid="back-button-container">
              <BackButton
                data-testid="back-button"
                link={returnPath}
                label=""
              />
            </div>

            <h1 className="text-xl sm:text-2xl lg:text-3xl text-[#1A1A1A]">
              {product?.name}
            </h1>
          </div>
          <p className="text-xs sm:text-sm text-gray-600 ml-11">
            {t('order_builder.description')}
          </p>
        </div>
      </div>
      <div className="flex flex-col lg:flex-row gap-4 sm:gap-6">
        <div className="flex-1 overflow-y-auto space-y-4 sm:space-y-6">
          <ProductDetails
            listingCount={listingCount}
            sellersCount={sellersCount}
            platformProduct={product}
          />
          <PricesAndAvailability
            handleLengthFilterChange={handleLengthFilterChange}
            selectedLength={selectedLength}
            platformProductLengths={
              (platformProductLengths?.sellerInventoryProductLengthsByPlatformProduct as PlatformProductLength[]) ||
              []
            }
            orderInfo={orderInfo}
            carts={(cartByLocation?.carts as Cart[]) || []}
            sellerInventories={
              sortedSellerProductInventories as SellerProductInventory[]
            }
          />
        </div>
        <div className="w-full lg:w-96 flex-shrink-0 space-y-6">
          <InventoryRequirement
            inventory={inventory?.buyerProductInventory as Inventory}
          />
          <OrderCalculator
            onOrderInfoChange={handleOrderInfoChange}
            onTargetOrderSizeBlur={handleTargetOrderSizeBlur}
            orderInfo={orderInfo}
            averageNPrice={averageNPrice}
            nCount={nCount}
            formattedMarketMidPriceWithUnit={formattedMarketMidPrice}
            carts={cartByLocation?.carts as Cart[]}
          />
        </div>
      </div>
    </div>
  );
}

export default function OrderBuilder() {
  const [searchParams] = useSearchParams();
  const inventoryId = searchParams.get('inventoryId');
  const platformProductLengthId = searchParams.get('platformProductLengthId');
  const platformProductId = searchParams.get('platformProductId');
  const returnPath = searchParams.get('returnPath');

  if (!platformProductId || !returnPath) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <h1>Missing required parameters</h1>
      </div>
    );
  }

  return (
    <OrderBuilderPage
      platformProductId={platformProductId}
      inventoryId={inventoryId}
      platformProductLengthId={platformProductLengthId}
      returnPath={returnPath}
    />
  );
}
